import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { filterTemplateSnapshot } from '@/services/formTemplateService';
import { bulkGetFormData } from '@/services/formDataService';
import { useAuthStore } from '@/stores/authStore';

export interface EmployeeOnboardingForm {
  formConfigId: string;
  formName: string;
  accessLevel: string;
  tabName: string;
  status: 'saved' | 'pending';
}

/**
 * Fetches default template snapshot + bulk form data for the employee,
 * then derives which forms are pending (no form data saved yet).
 * Employee must fill ALL forms before activation.
 */
export function useEmployeeOnboardingForms() {
  const session = useAuthStore((s) => s.session);
  const employeeId = session?.user.employeeId ?? '';

  // Fetch template snapshot filtered by isDefault=true
  const snapshotQuery = useQuery({
    queryKey: ['templateSnapshot', 'filter', 'EMPLOYEE', employeeId, { isDefault: true }],
    queryFn: () => filterTemplateSnapshot('EMPLOYEE', employeeId, { attributes: { isDefault: true } }),
    enabled: !!employeeId,
  });

  // Extract all formConfigIds from the snapshot (resolve employee-specific access)
  const allFormConfigIds = useMemo(() => {
    if (!snapshotQuery.data?.tabs) return [];
    return snapshotQuery.data.tabs.flatMap((tab) =>
      tab.forms
        .filter((f) => (f.accessOverrides?.EMPLOYEE ?? f.accessLevel) === 'READ_WRITE')
        .map((f) => f.formConfigId),
    );
  }, [snapshotQuery.data]);

  const bulkKeyString = allFormConfigIds.slice().sort().join(',');

  // Bulk fetch form data
  const bulkQuery = useQuery({
    queryKey: ['formData', 'bulk', 'EMPLOYEE', employeeId, bulkKeyString],
    queryFn: () =>
      bulkGetFormData('EMPLOYEE', employeeId, { formConfigIds: allFormConfigIds }),
    enabled: allFormConfigIds.length > 0 && !!employeeId,
    retry: 1,
  });

  // Derive onboarding forms with pending status
  const { forms, pendingForms } = useMemo(() => {
    if (!snapshotQuery.data?.tabs) return { forms: [], pendingForms: [] };

    const savedFormConfigIds = new Set(
      (bulkQuery.data ?? []).map((d) => d.formConfigId),
    );

    const allForms: EmployeeOnboardingForm[] = snapshotQuery.data.tabs.flatMap((tab) =>
      tab.forms
        .filter((f) => (f.accessOverrides?.EMPLOYEE ?? f.accessLevel) === 'READ_WRITE')
        .map((f) => ({
          formConfigId: f.formConfigId,
          formName: f.formName ?? 'Untitled Form',
          accessLevel: f.accessOverrides?.EMPLOYEE ?? f.accessLevel,
          tabName: tab.name,
          status: savedFormConfigIds.has(f.formConfigId)
            ? ('saved' as const)
            : ('pending' as const),
        })),
    );

    return {
      forms: allForms,
      pendingForms: allForms.filter((f) => f.status === 'pending'),
    };
  }, [snapshotQuery.data, bulkQuery.data]);

  const isLoading = snapshotQuery.isLoading || (allFormConfigIds.length > 0 && bulkQuery.isLoading);

  return {
    employeeId,
    forms,
    pendingForms,
    hasPendingForms: pendingForms.length > 0,
    isLoading,
    refetch: () => {
      void snapshotQuery.refetch();
      void bulkQuery.refetch();
    },
  };
}
